import type { ReactNode } from 'react';
import { RadioGroup } from '@heroui/react/radio-group';
import { Radio } from '@heroui/react/radio';
import { Label } from '@heroui/react/label';
import { Description } from '@heroui/react/description';
import { cn } from '@/utils/cn';

/**
 * A choice between two or three options, each needing a line of explanation —
 * Booking Mode ("Time Slot" vs "Turn / Queue"), approval mode, and similar.
 *
 * This exists because the card-shaped chooser is the single most-reinvented
 * control in the app, and every hand-rolled version has been a row of
 * `<button>`s painted to look selected. That markup is not a choice to
 * anything but a sighted mouse user: there is no `radiogroup`, no
 * `aria-checked`, arrow keys do nothing, and each card takes its own tab stop
 * so a keyboard user tabs through six controls to answer one question.
 *
 * Built on HeroUI's RadioGroup (react-aria), so the group is announced with
 * its label, arrow keys move the selection, and the whole group is one tab
 * stop. The card look is styling on top of a real radio — not a substitute
 * for one.
 */
export interface RadioCardOption<T extends string> {
  value: T;
  label: ReactNode;
  /** The one-line explanation. This is why the control is card-shaped. */
  description?: ReactNode;
  icon?: ReactNode;
  isDisabled?: boolean;
}

export function RadioCardGroup<T extends string>({
  label,
  ariaLabel,
  description,
  options,
  value,
  onChange,
  columns = 2,
  isDisabled,
  errorMessage,
  className,
}: {
  label?: ReactNode;
  /** Names the group when a heading above it already serves as the visible label. */
  ariaLabel?: string;
  description?: ReactNode;
  options: RadioCardOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Cards per row on md+. Single column below that, always. */
  columns?: 1 | 2 | 3;
  isDisabled?: boolean;
  errorMessage?: ReactNode;
  className?: string;
}) {
  return (
    <RadioGroup
      value={value}
      onChange={(next) => onChange(next as T)}
      isDisabled={isDisabled}
      isInvalid={!!errorMessage}
      aria-label={label ? undefined : ariaLabel}
      className={cn('flex w-full flex-col gap-3', className)}
    >
      {label && <Label>{label}</Label>}
      {description && <Description>{description}</Description>}

      <div
        className={cn(
          'grid gap-3',
          columns === 1 && 'grid-cols-1',
          columns === 2 && 'grid-cols-1 md:grid-cols-2',
          columns === 3 && 'grid-cols-1 md:grid-cols-3',
        )}
      >
        {options.map((option) => (
          // Radio is compound: Root is the field, Content is the focusable
          // radio button itself. The card styling goes on Content so the
          // whole card is the hit target and the focus ring frames the card.
          <Radio
            key={option.value}
            value={option.value}
            isDisabled={option.isDisabled}
            className="contents"
          >
            <Radio.Content
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-4',
                'border-default-200 bg-content1 text-start transition-colors',
                'hover:border-default-400',
                // Selection is carried by border AND tint together, never by
                // colour alone.
                'data-[selected=true]:border-primary data-[selected=true]:bg-primary/10',
                'focus-visible:outline-2 focus-visible:outline-offset-2',
                'focus-visible:outline-focus',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              <Radio.Control>
                <Radio.Indicator />
              </Radio.Control>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  {option.icon}
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-xs leading-snug text-default-500">
                    {option.description}
                  </span>
                )}
              </span>
            </Radio.Content>
          </Radio>
        ))}
      </div>

      {errorMessage && (
        <span className="text-xs text-danger">{errorMessage}</span>
      )}
    </RadioGroup>
  );
}
